import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Search, Eye, Handshake, FileSignature, Truck, MapPin, Flag } from "lucide-react";

const steps = [
  {
    num: "01",
    title: "Nos dices qué vehículo buscas",
    text: "Definimos contigo modelo, presupuesto y características clave.",
    icon: Search,
  },
  {
    num: "02",
    title: "Buscamos opciones en Alemania",
    text: "Filtramos miles de anuncios para presentarte las mejores opciones.",
    icon: MapPin,
  },
  {
    num: "03",
    title: "Revisamos el vehículo antes de decidir",
    text: "Inspección técnica y mecánica previa antes de cualquier pago.",
    icon: Eye,
  },
  {
    num: "04",
    title: "Gestionamos negociación y documentación",
    text: "Negociamos el precio y tramitamos toda la documentación legal.",
    icon: FileSignature,
  },
  {
    num: "05",
    title: "Coordinamos la importación hasta España",
    text: "Transporte, matriculación y entrega en la dirección que elijas.",
    icon: Truck,
  },
];

export const HowItWorks = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 70%", "end 30%"],
  });

  const lineWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const smoothLine = useSpring(lineWidth, { stiffness: 60, damping: 20 });
  const carX = useTransform(scrollYProgress, [0, 1], ["0%", "calc(100% - 32px)"]);
  const smoothCar = useSpring(carX, { stiffness: 60, damping: 20 });

  return (
    <section
      id="proceso"
      ref={ref}
      className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/5 overflow-hidden"
      data-testid="how-it-works-section"
    >
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] rounded-full pointer-events-none opacity-50"
        style={{ background: "radial-gradient(ellipse, rgba(212,175,55,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-20"
        >
          <span className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]">Cómo funciona</span>
          <h2 className="font-heading mt-6 text-3xl sm:text-5xl font-light tracking-tight text-[#FFFFF0] leading-tight">
            Un proceso claro en 5 pasos, de{" "}
            <span className="italic font-extralight text-[#D4AF37]">Alemania</span> a{" "}
            <span className="italic font-extralight text-[#D4AF37]">España</span>.
          </h2>
        </motion.div>

        {/* Animated journey header */}
        <div className="hidden lg:flex items-center justify-between mb-16 px-2">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3"
          >
            <Flag className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-heading text-xs uppercase tracking-[0.3em] text-[#FFFFF0]">DE — Alemania</span>
          </motion.div>

          <div className="flex-1 mx-6 relative">
            {/* Track */}
            <div className="h-px bg-white/10" />
            {/* Animated progress */}
            <motion.div
              style={{ width: smoothLine }}
              className="absolute top-0 left-0 h-px bg-gradient-to-r from-[#D4AF37] via-[#E5C158] to-[#D4AF37]"
            />
            {/* Moving car/truck */}
            <motion.div
              style={{ left: smoothCar }}
              className="absolute -top-[10px] z-10"
            >
              <div className="relative">
                <div className="absolute inset-0 blur-md bg-[#D4AF37]/60 rounded-full" />
                <div className="relative w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center border-2 border-[#0A0A0A]">
                  <Truck className="w-3 h-3 text-black" strokeWidth={2.5} />
                </div>
              </div>
            </motion.div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-heading text-xs uppercase tracking-[0.3em] text-[#737373] bg-[#0A0A0A] px-4 -translate-y-px">Ruta de importación</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3"
          >
            <span className="font-heading text-xs uppercase tracking-[0.3em] text-[#FFFFF0]">ES — España</span>
            <Flag className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          </motion.div>
        </div>

        {/* Steps grid */}
        <div className="relative">
          <div className="hidden lg:block absolute top-6 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex flex-col items-start group"
                data-testid={`step-${i + 1}`}
              >
                {/* Node with pulse */}
                <div className="relative z-10 mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 + i * 0.12, type: "spring", stiffness: 200 }}
                    className="relative"
                  >
                    <span className="absolute inset-0 rounded-full bg-[#D4AF37]/40 blur-md animate-pulse" />
                    <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#a8842c] flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      <s.icon className="w-5 h-5 text-black" strokeWidth={1.8} />
                    </div>
                  </motion.div>
                </div>

                <span className="font-heading text-5xl font-extralight text-[#D4AF37]/40 mb-3 group-hover:text-[#D4AF37]/70 transition-colors">
                  {s.num}
                </span>
                <h3 className="font-heading text-base sm:text-lg font-medium text-[#FFFFF0] mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-[#A3A3A3] leading-relaxed font-light">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
